import { datadogLogs } from '@datadog/browser-logs';
import axios from 'axios';
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { normalizeError } from './errors';
import type { BackendErrorResponse } from './errors';
import { userManager } from '@/auth/userManager';
import { API_BASE_URL } from '@/constants/api';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

const baseURL = API_BASE_URL;

// Create custom axios instance with response transformations
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const user = await userManager.getUser();
      if (!user?.access_token) {
        // OIDC callback hasn't settled (or session is gone). Bail out before
        // hitting the backend so we don't trigger a 401 → "Session expired"
        // toast during the initial login redirect.
        return Promise.reject({ _handledByToast: true });
      }
      config.headers.Authorization = `Bearer ${user.access_token}`;
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    },
  );

  // Add response interceptor to extract data and handle errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Extract data from the response
      return response.data;
    },
    async (err: AxiosError<BackendErrorResponse>) => {
      const status = err.status ?? err.response?.status ?? null;

      console.error(`Error ${status}:`, err);

      // Log to Datadog (production only) — metadata only, no raw payload
      if (import.meta.env.VITE_DATADOG_CLIENT_TOKEN && status) {
        const rawErrors = err.response?.data?.errors;
        const errorsCount = Array.isArray(rawErrors)
          ? rawErrors.length
          : rawErrors != null && typeof rawErrors === 'object'
            ? Object.keys(rawErrors).length
            : 0;
        datadogLogs.logger.error('API Error', {
          status,
          url: err.config?.url,
          method: err.config?.method,
          traceId: err.response?.data?.traceId,
          errorsCount,
        });
      }

      // Unauthorized — attempt silent token renewal before giving up
      if (status === 401) {
        if (!err.config?._retry) {
          try {
            await userManager.signinSilent();
            if (err.config) {
              err.config._retry = true;
              return instance(err.config);
            }
          } catch {
            // silent renew failed — session is gone, fall through to redirect
          }
        }
        window.dispatchEvent(new CustomEvent('api:unauthorized'));
        return Promise.reject({
          ...normalizeError(err),
          _handledByToast: true,
        });
      }

      // Forbidden — authenticated but lacks permission for this endpoint
      if (status === 403) {
        window.dispatchEvent(new CustomEvent('api:forbidden'));
        return Promise.reject({ _handledByToast: true });
      }

      // Unexpected server error — signal for a toast, reject so loading state clears
      if (status !== null && status >= 500) {
        window.dispatchEvent(
          new CustomEvent('api:server-error', { detail: { status } }),
        );
        return Promise.reject({ _handledByToast: true });
      }

      // Normalize error for consistent handling
      const normalizedError = normalizeError(err);

      return Promise.reject(normalizedError);
    },
  );

  return instance;
};

// Create and export the axios client
export const api = createAxiosInstance();
